import { useState, useEffect, useCallback } from 'react';
import { localDB } from '../services/db.js';
import { syncEngine } from '../services/sync.js';

/**
 * Hook for interacting with a specific IndexedDB store.
 * Provides data state, loading state, and CRUD operations.
 */
export function useStorage(storeName) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const items = await localDB.getAll(storeName);
      setData(items);
      setError(null);
    } catch (err) {
      console.error(`[useStorage] Error refreshing ${storeName}:`, err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [storeName]);

  useEffect(() => {
    refresh();

    // Listen for sync completion to update the UI
    const unsubscribe = syncEngine.onSyncComplete(refresh);
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [refresh]);

  const saveItem = useCallback(async (item) => {
    await localDB.put(storeName, item);
    await refresh();
  }, [storeName, refresh]);

  const removeItem = useCallback(async (id) => {
    await localDB.delete(storeName, id);
    await refresh();
  }, [storeName, refresh]);

  const clearAll = useCallback(async () => {
    await localDB.clear(storeName);
    await refresh();
  }, [storeName, refresh]);

  return {
    data,
    loading,
    error,
    refresh,
    saveItem,
    removeItem,
    clearAll,
  };
}

/**
 * Specialized hook for the synchronization queue.
 */
export function useSyncQueue() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const items = await localDB.getQueue();
      setQueue(items);
    } catch (err) {
      console.error('[useSyncQueue] Error refreshing queue:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();

    // Listen for sync completion to update the UI
    const unsubscribe = syncEngine.onSyncComplete(refresh);
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [refresh]);

  const addToQueue = useCallback(async (action) => {
    await localDB.addToQueue(action);
    await refresh();
  }, [refresh]);

  const removeFromQueue = useCallback(async (id) => {
    await localDB.removeFromQueue(id);
    await refresh();
  }, [refresh]);

  return {
    queue,
    loading,
    refresh,
    addToQueue,
    removeFromQueue,
  };
}
